import React, { useState, useEffect } from 'react';
import { 
    Box, 
    Typography, 
    Grid, 
    Paper,
    Divider,
    Chip
} from '@mui/material';
import { SandboxConsentDetails } from '../../../services/sandbox-consents-service';
import { languageService, Language } from '../../../services/language-service';

interface DetailsTabProps {
    consentData: SandboxConsentDetails;
}

const DetailsTab: React.FC<DetailsTabProps> = ({ consentData }) => {
    const { details } = consentData;
    const [languages, setLanguages] = useState<Language[]>([]);

    // Load languages on component mount
    useEffect(() => {
        const loadLanguages = async () => {
            try {
                const languageData = await languageService.getSupportedLanguages();
                setLanguages(languageData);
            } catch (error) {
                console.error('Failed to load languages:', error);
            }
        };
        loadLanguages();
    }, []);

    // Get language name from code
    const getLanguageName = (languageCode: string): string => {
        // Hardcode English for 'en' as requested
        if (languageCode.toLowerCase() === 'en') {
            return 'English';
        }
        
        const language = languages.find(lang => 
            lang.code.toLowerCase() === languageCode.toLowerCase()
        );
        
        if (language) {
            if (language.name !== language.localeName) {
                return `${language.name} (${language.localeName})`;
            }
            return language.name;
        }
        
        // Fallback to uppercase code if not found
        return languageCode.toUpperCase();
    };

    return (
        <Box>
            <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                Basic Consent Details
            </Typography>

            <Grid container spacing={3}>
                {/* Consent Header Information */}
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium', color: 'primary.main' }}>
                            Consent Header
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Consent ID</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                    #{details.consentHeader.consentHeaderId}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Consent Timestamp</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                    {new Date(details.consentHeader.consentTimestamp).toLocaleString()}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Consent Source</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                    {details.consentHeader.consentSource}
                                </Typography>
                            </Box>
                            {(details.consentHeader.consentSource === 'NOTICE' || details.consentHeader.consentSource === 'NOTICE_OTP') && details.dataPrincipal?.isMinor === true && (
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Provenance</Typography>
                                    <Box sx={{ mt: 0.5 }}>
                                        <Chip
                                            label="Guardian on behalf of minor"
                                            size="small"
                                            color="info"
                                            variant="outlined"
                                        />
                                    </Box>
                                </Box>
                            )}
                            <Box>
                                <Typography variant="caption" color="text.secondary">Language</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                    {getLanguageName(details.consentHeader.languageCode)}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Created Date</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                    {new Date(details.consentHeader.createdDate).toLocaleString()}
                                </Typography>
                            </Box>
                        </Box>
                    </Paper>
                </Grid>

                {/* Data Principal Information */}
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium', color: 'primary.main' }}>
                            Data Principal
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Box>
                                <Typography variant="caption" color="text.secondary">DP ID</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                    #{details.dataPrincipal.dpId}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Identifier Type</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                    {details.dataPrincipal.identifierType}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Identifier Value</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                    {details.dataPrincipal.identifierValue}
                                </Typography>
                            </Box>
                        </Box>
                    </Paper>
                </Grid>

                {/* Client Information */}
                <Grid item xs={12}>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium', color: 'primary.main' }}>
                            Client Information
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Client ID</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                        #{details.client.clientId}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Client Name</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                        {details.client.clientName}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Client Domain</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                        {details.client.clientDomain}
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

export default DetailsTab;
